import React, { useEffect, useState } from 'react';
import { Bookmark, BookmarkCheck, LogIn } from 'lucide-react';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { BookCover, EmptyState, Spinner } from '../components/UI';
import BookSubjectSearch from '../components/BookSubjectSearch';
import { PageHeader } from '../components/Layout';
import { bookmarkPayload, searchOpenLibrary } from '../services/openLibrary';

export default function SearchResultsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const legacySubject = (params.get('subject') || '').trim();
  const searchQuery = (params.get('q') || legacySubject).trim();
  const requestedMode = params.get('mode');
  const mode = ['name', 'content', 'subject'].includes(requestedMode) ? requestedMode : legacySubject ? 'subject' : 'name';
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [bookmarked, setBookmarked] = useState(new Set());
  const [busy, setBusy] = useState(new Set());
  const [loading, setLoading] = useState(!!searchQuery);
  const [searchError, setSearchError] = useState('');
  const [bookmarkError, setBookmarkError] = useState('');

  useEffect(() => {
    if (!searchQuery) { setBooks([]); setTotal(0); setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    setSearchError('');
    searchOpenLibrary(searchQuery, mode, controller.signal)
      .then((result) => { setBooks(result.books); setTotal(result.total); setExpanded(result.expanded); })
      .catch((err) => { if (err.name !== 'AbortError') setSearchError(err.message || 'Unable to search books.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [mode, searchQuery]);

  useEffect(() => {
    if (!user?.uid) {
      setBookmarked(new Set());
      setBookmarkError('');
      return undefined;
    }
    setBookmarkError('');
    return onSnapshot(collection(db, 'users', user.uid, 'bookmarks'), (snapshot) => {
      setBookmarked(new Set(snapshot.docs.map((item) => item.id)));
    }, (err) => {
      setBookmarkError(err.message || 'Unable to load bookmarks.');
    });
  }, [user?.uid]);

  const toggleBookmark = async (book) => {
    if (!user?.uid || busy.has(book.id)) return;
    setBusy((current) => new Set(current).add(book.id));
    setBookmarkError('');
    try {
      const bookmarkRef = doc(db, 'users', user.uid, 'bookmarks', book.id);
      if (bookmarked.has(book.id)) await deleteDoc(bookmarkRef);
      else await setDoc(bookmarkRef, { ...bookmarkPayload(book), bookmarkedAt: serverTimestamp(), createdAt: serverTimestamp() });
    } catch (err) {
      setBookmarkError(err.message || 'Unable to update this bookmark.');
    } finally {
      setBusy((current) => { const next = new Set(current); next.delete(book.id); return next; });
    }
  };

  return (
    <div className="page search-page">
      <PageHeader eyebrow="OPEN LIBRARY" title="Book search">
        {!user && <Link className="secondary-button" to="/login" state={{ from: location }}><LogIn size={16} /> Sign in</Link>}
      </PageHeader>
      <div className="page-body">
        <BookSubjectSearch key={`${mode}:${searchQuery}`} initialValue={searchQuery} initialMode={mode} compact />
        {searchQuery && !loading && !searchError && <p className="search-count">{expanded ? `Showing ${books.length} exact and related books` : `${total.toLocaleString()} books found`} for <strong>“{searchQuery}”</strong> by {mode === 'name' ? 'name' : mode === 'content' ? 'content' : 'subject'}</p>}
        {bookmarkError && <p className="form-error">Bookmarks unavailable: {bookmarkError}</p>}
        {loading ? <Spinner label="Searching Library" /> : searchError ? <EmptyState title="Search unavailable">{searchError}</EmptyState> : !searchQuery ? <EmptyState title="Search for books" /> : books.length ? (
          <div className="discovery-grid">
            {books.map((book) => (
              <article className="discovery-card" key={book.id}>
                <Link className="discovery-card-link" to={`/books/openlibrary/${book.workId || book.id}`} state={{ book }} aria-label={`View details for ${book.name}`}>
                  <BookCover book={book} />
                  <div className="discovery-card-copy">
                    <h2>{book.name}</h2>
                    <p>{book.author || 'Unknown author'}{book.firstPublishYear ? ` · ${book.firstPublishYear}` : ''}</p>
                    {!!book.categories.length && <div className="discovery-subjects">{book.categories.slice(0, 3).map((item) => <span key={item}>{item}</span>)}</div>}
                  </div>
                </Link>
                {user ? (
                  <button className={`bookmark-button ${bookmarked.has(book.id) ? 'active' : ''}`} onClick={() => toggleBookmark(book)} disabled={busy.has(book.id)}>
                    {bookmarked.has(book.id) ? <BookmarkCheck size={17} /> : <Bookmark size={17} />}
                    {busy.has(book.id) ? 'Saving…' : bookmarked.has(book.id) ? 'Bookmarked' : 'Bookmark'}
                  </button>
                ) : <Link className="bookmark-button" to="/login" state={{ from: location }}><Bookmark size={17} /> Sign in to bookmark</Link>}
              </article>
            ))}
          </div>
        ) : <EmptyState title="No books found" />}
      </div>
    </div>
  );
}
